import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

/**
 * Access profile assignment types.
 */

export const AssignmentStatus = {
    ACTIVE: 'ACTIVE',
    PENDING: 'PENDING',
    EXPIRED: 'EXPIRED',
    REVOKED: 'REVOKED',
} as const;
export type AssignmentStatus = typeof AssignmentStatus[keyof typeof AssignmentStatus];

export const AssignmentSourceType = {
    RULE: 'RULE',
    REQUEST: 'REQUEST',
    API: 'API',
    IMPORT: 'IMPORT',
} as const;
export type AssignmentSourceType = typeof AssignmentSourceType[keyof typeof AssignmentSourceType];

/**
 * Assignment source information.
 */
export interface AssignmentSource {
    id: UUID;
    sourceType: AssignmentSourceType;
    ruleId: UUID | null;
    ruleName: string | null;
    requestItemId: UUID | null;
    targetSystemId: UUID | null;
    assignedAt: Instant;
    removedAt: Instant | null;
    active: boolean;
}

export const AssignmentEventType = {
    CREATED: 'CREATED',
    EXTENDED: 'EXTENDED',
    SHORTENED: 'SHORTENED',
    REVOKED: 'REVOKED',
    RESTORED: 'RESTORED',
    SOURCE_ADDED: 'SOURCE_ADDED',
    SOURCE_REMOVED: 'SOURCE_REMOVED',
    SOURCE_RESTORED: 'SOURCE_RESTORED',
} as const;
export type AssignmentEventType = typeof AssignmentEventType[keyof typeof AssignmentEventType];

/**
 * Rich enum carrying UI rendering metadata (badge variant) for
 * {@link AssignmentEventType} wire values. Labels are resolved via the
 * i18n-backed {@code useAssignmentEventTypeLabel} hook since translations
 * live in the existing `functionGroupAssignments` namespace — keeping them
 * here would create a second source of truth.
 */
export class AssignmentEventTypeBadge extends EnumValue {
    static readonly CREATED = new AssignmentEventTypeBadge(AssignmentEventType.CREATED, BadgeVariant.SUCCESS)
    static readonly EXTENDED = new AssignmentEventTypeBadge(AssignmentEventType.EXTENDED, BadgeVariant.DEFAULT)
    static readonly SHORTENED = new AssignmentEventTypeBadge(AssignmentEventType.SHORTENED, BadgeVariant.WARNING)
    static readonly REVOKED = new AssignmentEventTypeBadge(AssignmentEventType.REVOKED, BadgeVariant.DESTRUCTIVE)
    static readonly RESTORED = new AssignmentEventTypeBadge(AssignmentEventType.RESTORED, BadgeVariant.SUCCESS)
    static readonly SOURCE_ADDED = new AssignmentEventTypeBadge(AssignmentEventType.SOURCE_ADDED, BadgeVariant.DEFAULT)
    static readonly SOURCE_REMOVED = new AssignmentEventTypeBadge(AssignmentEventType.SOURCE_REMOVED, BadgeVariant.WARNING)
    static readonly SOURCE_RESTORED = new AssignmentEventTypeBadge(AssignmentEventType.SOURCE_RESTORED, BadgeVariant.SUCCESS)

    static readonly values: ReadonlyArray<AssignmentEventTypeBadge> = [
        AssignmentEventTypeBadge.CREATED,
        AssignmentEventTypeBadge.EXTENDED,
        AssignmentEventTypeBadge.SHORTENED,
        AssignmentEventTypeBadge.REVOKED,
        AssignmentEventTypeBadge.RESTORED,
        AssignmentEventTypeBadge.SOURCE_ADDED,
        AssignmentEventTypeBadge.SOURCE_REMOVED,
        AssignmentEventTypeBadge.SOURCE_RESTORED,
    ]

    static of(code: AssignmentEventType | null | undefined): AssignmentEventTypeBadge | undefined {
        return AssignmentEventTypeBadge.values.find((v) => v.code === code)
    }
}

export interface BaseEvent {
    id: UUID;
    eventType: AssignmentEventType;
    oldExpiresAt: Instant | null;
    newExpiresAt: Instant | null;
    performedBy: EntityLink | null;
    comment: string | null;
    createdAt: Instant;
    sourceId: UUID | null;
    sourceType: AssignmentSourceType | null;
    sourceName: string | null;
}

export type AssignmentEvent = BaseEvent;

/**
 * List-shape access profile assignment — returned by
 * `GET /api/i/identities/{identityId}/assignments`. Carries the fields
 * rendered per row on the IdentityDetail Assignments tab, including
 * `sources` (rendered as badges in every row) and `events` (rendered on
 * row expand). The Detail shape only adds `version`. See 1120.07.99 for
 * the follow-up: a dedicated per-assignment GET endpoint would let us
 * drop `events` from the list payload.
 */
export interface AccessProfileAssignmentListItem {
    id: UUID;
    identityId: UUID;
    accessProfileId: UUID;
    accessProfileName: string;
    resourceName: string;
    systemName: string;
    startAt: Instant;
    expiresAt: Instant;
    revokedAt: Instant | null;
    status: AssignmentStatus;
    permanent: boolean;
    sources: AssignmentSource[];
    events: AssignmentEvent[];
}

/**
 * Wide detail-shape access profile assignment — returned by revoke/restore
 * and by `GET /api/i/my/access`. Adds `version` to the ListItem.
 */
export interface AccessProfileAssignment extends AccessProfileAssignmentListItem {
    version: number;
}

export interface IdentityTimeline {
    id: UUID
    eventType: string
    assignmentId: UUID
    accessProfileId: UUID
    accessProfileName: string
    resourceName: string
    systemName: string
    oldExpiresAt: Instant | null
    newExpiresAt: Instant | null
    performedBy: EntityLink | null
    comment: string | null
    createdAt: Instant
    sourceId: UUID | null
    sourceType: string | null
    sourceName: string | null
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AssignmentSourceResponseFields = {
    id: 'id',
    sourceType: 'sourceType',
    ruleId: 'ruleId',
    ruleName: 'ruleName',
    requestItemId: 'requestItemId',
    targetSystemId: 'targetSystemId',
    assignedAt: 'assignedAt',
    removedAt: 'removedAt',
    active: 'active',
} as const

export const AssignmentEventResponseFields = {
    id: 'id',
    eventType: 'eventType',
    oldExpiresAt: 'oldExpiresAt',
    newExpiresAt: 'newExpiresAt',
    performedBy: 'performedBy',
    comment: 'comment',
    causeType: 'causeType',
    causeId: 'causeId',
    createdAt: 'createdAt',
    sourceId: 'sourceId',
    sourceType: 'sourceType',
    sourceName: 'sourceName',
} as const

export const AccessProfileAssignmentResponseFields = {
    id: 'id',
    identityId: 'identityId',
    accessProfileId: 'accessProfileId',
    accessProfileName: 'accessProfileName',
    resourceName: 'resourceName',
    systemName: 'systemName',
    startAt: 'startAt',
    expiresAt: 'expiresAt',
    revokedAt: 'revokedAt',
    status: 'status',
    permanent: 'permanent',
    sources: 'sources',
    events: 'events',
    version: 'version',
} as const
/* eslint-enable i18next/no-literal-string */
